import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Lock, ChevronDown, ChevronUp, Fuel, Building2 } from 'lucide-react';
import AccessDeniedBlock from '../components/AccessDeniedBlock';
import UnsavedChangesDialog, { useUnsavedChanges } from '../components/UnsavedChangesDialog';

// Cores para os postos
const POSTO_COLORS = {
  'POSTO JP&HG LOBO': '#10B981',
  'POSTO CARAUARI': '#EF4444',
  'REDE BONS AMIGOS': '#3B82F6',
  'PONTÃO BARROTE': '#6B7280',
};

const DEFAULT_COLORS = ['#1e40af', '#047857', '#7c3aed', '#dc2626', '#0891b2', '#ea580c'];

export default function PostosPage() {
  const { canManageCadastros, isSecretaria } = useAuth();
  const canEdit = canManageCadastros();
  
  const [postos, setPostos] = useState([]);
  const [combustiveis, setCombustiveis] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [combDialogOpen, setCombDialogOpen] = useState(false);
  
  const [editingId, setEditingId] = useState(null);
  const [editingCombId, setEditingCombId] = useState(null);
  
  const [expandedId, setExpandedId] = useState(null);
  const [activePostoId, setActivePostoId] = useState(null);
  
  const [formData, setFormData] = useState({ name: '', cnpj: '', address: '', phone: '', email: '' });
  const [combFormData, setCombFormData] = useState({ type: '', unit: 'Litro', posto_id: '', valor_litro: '' });
  
  // Hook para controle de alterações não salvas
  const unsavedChanges = useUnsavedChanges();

  useEffect(() => { fetchData(); }, []);

  // Bloquear acesso para perfil secretaria
  if (isSecretaria()) {
    return (
      <AccessDeniedBlock 
        title="Acesso Restrito" 
        message="Usuarios com perfil Secretaria nao tem permissao para acessar esta pagina. Acesse a pagina de Secretarias para gerenciar seu departamento."
      />
    );
  }

  const fetchData = async () => {
    try {
      const [postosRes, combRes] = await Promise.all([
        api.get('/postos'),
        api.get('/combustiveis')
      ]);
      setPostos(postosRes.data);
      setCombustiveis(combRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  // === POSTO HANDLERS ===
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/postos/${editingId}`, formData);
        toast.success('Posto atualizado!');
      } else {
        await api.post('/postos', formData);
        toast.success('Posto cadastrado!');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Erro ao salvar posto');
    }
  };

  const handleEdit = (item, e) => {
    e.stopPropagation();
    setFormData(item);
    setEditingId(item.id);
    setDialogOpen(true);
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Deseja realmente excluir este posto?')) return;
    try {
      await api.delete(`/postos/${id}`);
      toast.success('Posto excluído!');
      fetchData();
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', cnpj: '', address: '', phone: '', email: '' });
    setEditingId(null);
    unsavedChanges.resetChanges();
  };

  // Função para lidar com mudanças no formulário
  const handleFormChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    unsavedChanges.markAsChanged();
  };

  // Função para tentar fechar o dialog
  const handleDialogClose = (open) => {
    if (!open && unsavedChanges.hasChanges) {
      unsavedChanges.tryAction(() => {
        setDialogOpen(false);
        resetForm();
      });
    } else {
      setDialogOpen(open);
      if (!open) resetForm();
    }
  };

  // === COMBUSTIVEL HANDLERS ===
  const handleCombSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSend = { 
        type: combFormData.type,
        unit: combFormData.unit || 'Litro',
        posto_id: activePostoId,
        valor_litro: parseFloat(combFormData.valor_litro) || 0
      };
      console.log('Salvando combustível:', dataToSend);
      if (editingCombId) {
        await api.put(`/combustiveis/${editingCombId}`, dataToSend);
        toast.success('Combustível atualizado!');
      } else {
        await api.post('/combustiveis', dataToSend);
        toast.success('Combustível cadastrado!');
      }
      setCombDialogOpen(false);
      resetCombForm();
      fetchData();
    } catch (error) {
      console.error('Erro ao salvar:', error);
      toast.error('Erro ao salvar combustível');
    }
  };

  const handleCombEdit = (item, e) => {
    e.stopPropagation();
    setCombFormData({
      type: item.type,
      unit: item.unit || 'Litro',
      posto_id: item.posto_id || '',
      valor_litro: item.valor_litro?.toString() || ''
    });
    setEditingCombId(item.id);
    setCombDialogOpen(true);
  };

  const handleCombDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Deseja realmente excluir este combustível?')) return;
    try {
      await api.delete(`/combustiveis/${id}`);
      toast.success('Combustível excluído!');
      fetchData();
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  const resetCombForm = () => {
    setCombFormData({ type: '', unit: 'Litro', posto_id: '', valor_litro: '' });
    setEditingCombId(null);
  };

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
    setActivePostoId(expandedId === id ? null : id);
  };

  const getCombustiveisByPosto = (postoId) => {
    return combustiveis.filter(c => c.posto_id === postoId);
  };

  const getPostoColor = (postoName, index) => {
    return POSTO_COLORS[postoName] || DEFAULT_COLORS[index % DEFAULT_COLORS.length];
  };

  const openAddComb = (postoId, e) => {
    e.stopPropagation();
    console.log('Abrindo form para posto:', postoId);
    setActivePostoId(postoId);
    resetCombForm();
    setCombDialogOpen(true);
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#003366]"></div></div>;

  return (
    <div className="space-y-6" data-testid="postos-page">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-[#003366]" style={{ fontFamily: 'Manrope, sans-serif' }}>Postos de Combustível</h2>
          <p className="text-slate-600">Clique em um posto para ver e gerenciar combustíveis</p>
        </div>
        {canEdit ? (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-[#0066CC] hover:bg-[#0052A3]" data-testid="add-posto-button">
                <Plus className="mr-2" size={20} />Novo Posto
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle>{editingId ? 'Editar' : 'Novo'} Posto</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="posto-form">
                <div className="space-y-2">
                  <Label>Nome *</Label>
                  <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>CNPJ</Label>
                    <Input value={formData.cnpj} onChange={(e) => setFormData({ ...formData, cnpj: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>Telefone</Label>
                    <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Endereço</Label>
                  <Input value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>E-mail</Label>
                  <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                </div>
                <div className="flex gap-2 justify-end">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
                  <Button type="submit" className="bg-[#003366] hover:bg-[#002244]">Salvar</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        ) : (
          <div className="flex items-center gap-2 text-amber-600 bg-amber-50 px-4 py-2 rounded-lg">
            <Lock size={18} />
            <span className="text-sm font-medium">Modo Visualização</span>
          </div>
        )}
      </div>

      {/* Dialog Combustível */}
      <Dialog open={combDialogOpen} onOpenChange={setCombDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editingCombId ? 'Editar' : 'Novo'} Combustível</DialogTitle></DialogHeader>
          <form onSubmit={handleCombSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Tipo de Combustível *</Label>
              <Input 
                value={combFormData.type} 
                onChange={(e) => setCombFormData({ ...combFormData, type: e.target.value })} 
                placeholder="Ex: Gasolina Comum, Diesel S10..."
                required 
              />
            </div>
            <div className="space-y-2">
              <Label>Valor por Litro (R$) *</Label>
              <Input 
                type="number"
                step="0.01"
                value={combFormData.valor_litro} 
                onChange={(e) => setCombFormData({ ...combFormData, valor_litro: e.target.value })} 
                placeholder="Ex: 5.89"
                required 
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setCombDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" className="bg-[#003366] hover:bg-[#002244]">Salvar</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Lista de Postos */}
      <div className="space-y-4">
        {postos.map((posto, index) => {
          const color = getPostoColor(posto.name, index);
          const isExpanded = expandedId === posto.id;
          const combList = getCombustiveisByPosto(posto.id);
          
          return (
            <Card 
              key={posto.id} 
              className="cursor-pointer transition-all duration-300 hover:shadow-lg border-l-4"
              style={{ borderLeftColor: color }}
              onClick={() => toggleExpand(posto.id)}
              data-testid={`posto-card-${posto.id}`}
            >
              <CardHeader className="pb-2">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <Building2 size={24} style={{ color: color }} />
                    <div>
                      <CardTitle className="text-xl font-bold" style={{ color: color }}>
                        {posto.name}
                      </CardTitle>
                      <p className="text-sm text-gray-500">
                        {posto.cnpj && `CNPJ: ${posto.cnpj}`} {posto.phone && `• ${posto.phone}`}
                      </p>
                      {posto.address && <p className="text-sm text-gray-400">{posto.address}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Fuel size={16} style={{ color: color }} />
                      <span>{combList.length} combustível(is)</span>
                    </div>
                    {canEdit && (
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={(e) => handleEdit(posto, e)}>
                          <Pencil size={16} className="text-gray-500" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={(e) => handleDelete(posto.id, e)}>
                          <Trash2 size={16} className="text-red-500" />
                        </Button>
                      </div>
                    )}
                    {isExpanded ? <ChevronUp size={20} style={{ color: color }} /> : <ChevronDown size={20} style={{ color: color }} />}
                  </div>
                </div>
              </CardHeader>
              
              {isExpanded && (
                <CardContent className="pt-4 border-t">
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="font-semibold flex items-center gap-2" style={{ color: color }}>
                      <Fuel size={18} />
                      Combustíveis Disponíveis ({combList.length})
                    </h4>
                    {canEdit && (
                      <Button size="sm" variant="outline" onClick={(e) => openAddComb(posto.id, e)} className="text-xs">
                        <Plus size={14} className="mr-1" /> Adicionar Combustível
                      </Button>
                    )}
                  </div>
                  
                  {combList.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {combList.map(comb => (
                        <div 
                          key={comb.id} 
                          className="bg-gray-50 rounded-lg p-4 border-l-4 flex justify-between items-center group"
                          style={{ borderLeftColor: color }}
                        >
                          <div>
                            <p className="font-medium text-gray-800">{comb.type}</p>
                            <p className="text-lg font-bold" style={{ color: color }}>
                              R$ {(comb.valor_litro || 0).toFixed(2)}/L
                            </p>
                          </div>
                          {canEdit && (
                            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Button size="sm" variant="ghost" onClick={(e) => handleCombEdit(comb, e)}>
                                <Pencil size={14} />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={(e) => handleCombDelete(comb.id, e)}>
                                <Trash2 size={14} className="text-red-500" />
                              </Button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-400 text-sm italic py-4 text-center">Nenhum combustível cadastrado neste posto</p>
                  )}
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>

      {postos.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center text-slate-500">
            <Building2 size={48} className="mx-auto mb-4 opacity-50" />
            <p>Nenhum posto cadastrado</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
